
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Link } from 'react-router-dom';
import Header from './components/Header';
import StatsCard from './components/StatsCard';
import { useAuth } from '../../hooks/useAuth';

// Helper function to get profile photos
function getProfilePhoto(name: string): string | null {
  const photoMap: { [key: string]: string } = {
    'Christine Bautista':
      'https://public.readdy.ai/ai/img_res/66613fd7a3a5f1d69c19eaf38ad19818.jpg',
    'Jeremy Bautista':
      'https://readdy.ai/api/search-image?query=Professional%20Filipino%20man%20with%20short%20dark%20hair%2C%20wearing%20business%20shirt%2C%20confident%20smile%2C%20office%20headshot%20style%2C%20clean%20white%20background%2C%20high%20quality%20portrait%20photography&width=150&height=150&seq=jeremy-bautista&orientation=squarish',
  };

  return photoMap[name] || null;
}

// Ranking Table Component (true table format)
function RankingTable({ employees }: { employees: any[] }) {
  // Create comprehensive ranking data
  const createComprehensiveRanking = (employees: any[]) => {
    return [...employees]
      .filter((emp) => emp.underwriting_assistant !== 'Total')
      .map((emp) => {
        const policies =
          typeof emp.policies === 'string'
            ? parseInt(emp.policies.replace(/[^0-9]/g, '')) || 0
            : emp.policies || 0;

        const brokerFeeAverage =
          typeof emp.broker_fee_average === 'string'
            ? parseFloat(emp.broker_fee_average.replace(/[$,]/g, '')) || 0
            : emp.broker_fee_average || 0;

        const roadsides = emp.roadsides || 0;

        return {
          name: emp.underwriting_assistant || 'Unknown',
          policies,
          brokerFeeAverage,
          roadsides,
          photo: getProfilePhoto(emp.underwriting_assistant || 'Unknown'),
        };
      })
      .sort((a, b) => {
        // Primary sort: Policies (descending)
        if (b.policies !== a.policies) return b.policies - a.policies;
        // Secondary sort: Roadsides (descending)
        if (b.roadsides !== a.roadsides) return b.roadsides - a.roadsides;
        // Tertiary sort: Broker Fee Average (descending)
        return b.brokerFeeAverage - a.brokerFeeAverage;
      });
  };

  const rankedEmployees = createComprehensiveRanking(employees);

  const getRankIcon = (rank: number) => {
    if (rank === 1) return '🏆';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  const maxPolicies = Math.max(...rankedEmployees.map((e) => e.policies), 1);

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200">
        <h3 className="text-base font-bold text-gray-900">PH Performance Ranking</h3>
        <p className="text-xs text-gray-600 mt-1">Ranked by: 1) Policy Count, 2) Roadside, 3) BF Average</p>
        <p className="text-xs text-blue-600 font-medium mt-1">
          Showing {rankedEmployees.length} underwriting assistants
        </p>
      </div>

      {/* List Items */}
      <div className="divide-y divide-gray-200">
        {rankedEmployees.map((employee, index) => {
          const rank = index + 1;
          const isTopThree = rank <= 3;
          const rankIcon = getRankIcon(rank);

          return (
            <div key={index} className={`px-4 py-3 ${isTopThree ? 'bg-yellow-50' : 'bg-white'}`}>
              {/* Agent Info Row */}
              <div className="flex items-center mb-2">
                {/* Rank Badge */}
                <div className="flex-shrink-0 mr-2">
                  {rankIcon ? (
                    <span className="text-xl">{rankIcon}</span>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-blue-600">{rank}</span>
                    </div>
                  )}
                </div>

                {/* Avatar */}
                <div className="flex-shrink-0 mr-3">
                  <img
                    className="h-10 w-10 rounded-full object-cover"
                    src={
                      employee.photo ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        employee.name,
                      )}&background=3b82f6&color=ffffff&size=128`
                    }
                    alt={employee.name}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        employee.name,
                      )}&background=3b82f6&color=ffffff&size=128`;
                    }}
                  />
                </div>

                {/* Name */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900">{employee.name}</div>
                </div>
              </div>

              {/* Metrics Row */}
              <div className="flex items-center justify-end space-x-4 text-xs">
                {/* Policies */}
                <div className="flex items-center">
                  <i className="ri-file-text-fill text-blue-500 mr-1"></i>
                  <span className="font-medium text-gray-900">{employee.policies}</span>
                </div>

                {/* Roadsides */}
                <div className="flex items-center">
                  <i className="ri-roadster-fill text-red-500 mr-1"></i>
                  <span className="font-medium text-gray-900">{employee.roadsides}</span>
                </div>

                {/* BF Average */}
                <div className="flex items-center">
                  <i className="ri-money-dollar-box-fill text-green-500 mr-1"></i>
                  <span className="font-medium text-gray-900">${employee.brokerFeeAverage}</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-2">
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${
                      rank === 1
                        ? 'bg-yellow-400'
                        : rank === 2
                        ? 'bg-gray-400'
                        : rank === 3
                        ? 'bg-orange-400'
                        : 'bg-blue-400'
                    }`}
                    style={{ width: `${(employee.policies / maxPolicies) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Individual Employee Performance Cards Component
function EmployeePerformanceCards({ employees }: { employees: any[] }) {
  const individualEmployees = employees.filter((emp) => emp.underwriting_assistant !== 'Total');

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {individualEmployees.map((employee, index) => {
        const policies =
          typeof employee.policies === 'string'
            ? parseInt(employee.policies.replace(/[^0-9]/g, '')) || 0
            : employee.policies || 0;

        const brokerFeeTotal =
          typeof employee.broker_fee === 'string'
            ? parseFloat(employee.broker_fee.replace(/[$,]/g, '')) || 0
            : employee.broker_fee || 0;

        const brokerFeeAverage =
          typeof employee.broker_fee_average === 'string'
            ? parseFloat(employee.broker_fee_average.replace(/[$,]/g, '')) || 0
            : employee.broker_fee_average || 0;

        const roadsides = employee.roadsides || 0;
        const photo = getProfilePhoto(employee.underwriting_assistant || 'Unknown');
        const employeeName = employee.underwriting_assistant || 'Unknown Employee';

        return (
          <div
            key={index}
            className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden bg-blue-100">
                {photo ? (
                  <img
                    src={photo}
                    alt={employeeName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `<i class="ri-user-line text-blue-600 text-xl"></i>`;
                      }
                    }}
                  />
                ) : (
                  <i className="ri-user-line text-blue-600 text-xl"></i>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold truncate text-gray-900">{employeeName}</h3>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 flex items-center">📋 Policies:</span>
                <span className="text-lg font-bold text-gray-900">{policies}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 flex items-center">📈 BF Average:</span>
                <span className="text-lg font-bold text-gray-900 flex items-center">
                  {brokerFeeAverage < 130 && <span className="mr-1">💩</span>}
                  ${brokerFeeAverage}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 flex items-center">💰 BF Total:</span>
                <span className="text-lg font-bold text-gray-900">${brokerFeeTotal.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 flex items-center">🚗 Roadsides:</span>
                <span className="text-lg font-bold text-gray-900">{roadsides}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Individual Underwriting Assistant Performance Component
function IndividualPerformance({ data }: { data: any[] }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Individual Underwriting Assistant Performance</h2>
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  // Filter out "Total" row and sort alphabetically by name
  const filteredData = data
    .filter(item => 
      item.underwriting_assistant && 
      item.underwriting_assistant.toLowerCase() !== 'total'
    )
    .sort((a, b) => {
      const nameA = (a.underwriting_assistant || '').toLowerCase();
      const nameB = (b.underwriting_assistant || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900">Individual Underwriting Assistant Performance</h2>
        <p className="text-sm text-gray-600 mt-1">Detailed performance metrics for each underwriting assistant</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredData.map((employee, index) => {
          const employeeName = employee.underwriting_assistant || 'Unknown';
          const employeePhoto = getProfilePhoto(employeeName);
          
          const policies = parseInt(String(employee.policies || '0').replace(/[^0-9]/g, '')) || 0;
          const roadsides = parseInt(String(employee.roadsides || '0').replace(/[^0-9]/g, '')) || 0;
          
          // Parse broker fee safely
          let brokerFee = 0;
          let bfAverage = 0;
          if (employee.broker_fee) {
            const brokerFeeStr = String(employee.broker_fee).replace(/[$,]/g, '');
            brokerFee = parseFloat(brokerFeeStr) || 0;
          }
          if (policies > 0) {
            bfAverage = brokerFee / policies;
          }

          return (
            <div 
              key={`${employeeName}-${index}`}
              className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Employee Header */}
              <div className="flex items-center mb-4">
                {employeePhoto ? (
                  <img
                    src={employeePhoto}
                    alt={employeeName}
                    className="h-16 w-16 rounded-full object-cover mr-3"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(employeeName)}&background=3b82f6&color=ffffff&size=128`;
                    }}
                  />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                    <i className="ri-user-line text-2xl text-blue-600"></i>
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-gray-900">{employeeName}</h3>
                  <p className="text-sm text-gray-500">Underwriting Assistant</p>
                </div>
              </div>

              {/* Metrics */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">📋 Policies</span>
                  <span className="font-semibold text-gray-900">{policies}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    📈 BF Average {bfAverage < 130 && bfAverage > 0 ? '💩' : ''}
                  </span>
                  <span className="font-semibold text-gray-900">
                    ${Math.round(bfAverage).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">💰 BF Total</span>
                  <span className="font-semibold text-gray-900">
                    ${Math.round(brokerFee).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">🚗 Roadsides</span>
                  <span className="font-semibold text-gray-900">{roadsides}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PhilippinesSalesTodayPage() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckComplete, setAdminCheckComplete] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        try {
          // Since user_profiles table doesn't have a role column,
          // we'll check if the user is an admin based on their email
          // This is a temporary solution - you may want to add a role column later
          const adminEmails = ['admin@example.com', 'christine@example.com', 'jeremy@example.com'];
          setIsAdmin(adminEmails.includes(user.email || ''));
        } catch (err) {
          console.error('Error checking admin status:', err);
        }
      }
      setAdminCheckComplete(true);
    };

    checkAdminStatus();
  }, [user]);

  useEffect(() => {
    // Always fetch data, don't wait for user
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/philippines-sales-today-data`, {
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status ${response.status}`);
      }

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error);
      }

      setData(result.data || []);
    } catch (err) {
      console.error('Error fetching Philippines sales today data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchData();
  };

  // Wait for both data and admin check to complete
  if (loading || !adminCheckComplete) {
    return (
      <div className="min-h-screen bg-orange-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p className="text-white font-medium">Loading Philippines sales data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-orange-400">
        <Header onRefresh={handleRefresh} isConnected={!error} showDashboardButton={isAdmin} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <i className="ri-error-warning-line text-red-600 text-xl mr-3"></i>
              <div>
                <h3 className="text-red-800 font-semibold">Error Loading Data</h3>
                <p className="text-red-600 mt-1">{error}</p>
                <button
                  onClick={fetchData}
                  className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get totals from the "Total" row
  const totalRow = data.find(
    (item) => item.underwriting_assistant && item.underwriting_assistant.toLowerCase() === 'total',
  );

  // Calculate stats from the Total row
  const stats = {
    totalPolicies:
      totalRow?.policies
        ? typeof totalRow.policies === 'string'
          ? parseInt(totalRow.policies.replace(/[^0-9]/g, ''))
          : totalRow.policies
        : 0,
    totalBrokerFee:
      totalRow?.broker_fee
        ? typeof totalRow.broker_fee === 'string'
          ? parseFloat(totalRow.broker_fee.replace(/[$,]/g, ''))
          : totalRow.broker_fee
        : 0,
    bfAverage:
      totalRow?.broker_fee_average
        ? typeof totalRow.broker_fee_average === 'string'
          ? parseFloat(totalRow.broker_fee_average.replace(/[$,]/g, ''))
          : totalRow.broker_fee_average
        : 0,
    totalRoadsides: totalRow?.roadsides || 0,
  };

  // Calculate top performers
  const individualEmployees = data.filter((emp) => emp.underwriting_assistant !== 'Total');

  const topPoliciesData =
    individualEmployees.length > 0
      ? (() => {
          const sorted = [...individualEmployees].sort((a, b) => {
            const aPolicies =
              typeof a.policies === 'string' ? parseInt(a.policies.replace(/[^0-9]/g, '')) : a.policies || 0;
            const bPolicies =
              typeof b.policies === 'string' ? parseInt(b.policies.replace(/[^0-9]/g, '')) : b.policies || 0;
            return bPolicies - aPolicies;
          });
          const top = sorted[0];
          return {
            name: top.underwriting_assistant || 'Unknown',
            policies:
              typeof top.policies === 'string' ? parseInt(top.policies.replace(/[^0-9]/g, '')) : top.policies || 0,
            photo: getProfilePhoto(top.underwriting_assistant || 'Unknown'),
          };
        })()
      : null;

  const topRoadsidesData =
    individualEmployees.length > 0
      ? (() => {
          const sorted = [...individualEmployees].sort((a, b) => {
            const aRoadsides = a.roadsides || 0;
            const bRoadsides = b.roadsides || 0;
            return bRoadsides - aRoadsides;
          });
          const top = sorted[0];
          return {
            name: top.underwriting_assistant || 'Unknown',
            roadsides: top.roadsides || 0,
            photo: getProfilePhoto(top.underwriting_assistant || 'Unknown'),
          };
        })()
      : null;

  const topBrokerFeeTotalData =
    individualEmployees.length > 0
      ? (() => {
          const sorted = [...individualEmployees].sort((a, b) => {
            const aBrokerFee =
              typeof a.broker_fee === 'string' ? parseFloat(a.broker_fee.replace(/[$,]/g, '')) : a.broker_fee || 0;
            const bBrokerFee =
              typeof b.broker_fee === 'string' ? parseFloat(b.broker_fee.replace(/[$,]/g, '')) : b.broker_fee || 0;
            return bBrokerFee - aBrokerFee;
          });
          const top = sorted[0];
          return {
            name: top.underwriting_assistant || 'Unknown',
            brokerFeeTotal:
              typeof top.broker_fee === 'string'
                ? parseFloat(top.broker_fee.replace(/[$,]/g, ''))
                : top.broker_fee || 0,
            photo: getProfilePhoto(top.underwriting_assistant || 'Unknown'),
          };
        })()
      : null;

  // Combine top performer data into a single object for UI rendering
  const topPerformers = {
    mostPolicies: topPoliciesData,
    mostRoadsides: topRoadsidesData,
    highestBFTotal: topBrokerFeeTotalData,
  };

  // Prepare employee cards data
  const employeeCardsData = individualEmployees.map((employee, index) => {
    const policies =
      typeof employee.policies === 'string' ? parseInt(employee.policies.replace(/[^0-9]/g, '')) || 0 : employee.policies || 0;

    const brokerFeeTotal =
      typeof employee.broker_fee === 'string'
        ? parseFloat(employee.broker_fee.replace(/[$,]/g, '')) || 0
        : employee.broker_fee || 0;

    const brokerFeeAverage =
      typeof employee.broker_fee_average === 'string'
        ? parseFloat(employee.broker_fee_average.replace(/[$,]/g, '')) || 0
        : employee.broker_fee_average || 0;

    const roadsides = employee.roadsides || 0;
    const photo = getProfilePhoto(employee.underwriting_assistant || 'Unknown');
    const employeeName = employee.underwriting_assistant || 'Unknown Employee';

    return {
      id: index,
      name: employeeName,
      policies,
      brokerFeeTotal,
      brokerFeeAverage,
      roadsides,
      photo,
    };
  });

  return (
    <div className="min-h-screen bg-orange-400">
      <Header onRefresh={handleRefresh} isConnected={!error} showDashboardButton={isAdmin} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Philippines Sales Today</h1>
          <p className="text-gray-600 mt-2">Today's sales performance from Philippines team</p>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row justify-center items-center mb-6 space-y-4 lg:space-y-0">
            <div className="flex-shrink-0 flex flex-wrap justify-center gap-3 max-w-6xl">
              <Link
                to="/philippines-sales"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-bar-chart-line"></i>
                <span>PH Sales MTD</span>
              </Link>

              {isAdmin && (
                <Link
                  to="/dashboard"
                  className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <i className="ri-dashboard-line"></i>
                  <span>Dashboard</span>
                </Link>
              )}

              <Link
                to="/mtd-sales"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-calendar-check-line"></i>
                <span>MTD Sales</span>
              </Link>

              <Link
                to="/todays-sales"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-calendar-todo-line"></i>
                <span>Today's Sales</span>
              </Link>

              <Link
                to="/retention-mtd"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-user-heart-line"></i>
                <span>Retention MTD</span>
              </Link>

              <Link
                to="/retention-today"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-user-heart-line"></i>
                <span>Retention Today</span>
              </Link>

              {isAdmin && (
                <Link
                  to="/suggestions-admin"
                  className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <i className="ri-lightbulb-line"></i>
                  <span>Suggestions</span>
                </Link>
              )}

              <Link
                to="/tv-view"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2 border-2 border-purple-300"
              >
                <i className="ri-tv-line"></i>
                <span>TV View</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <StatsCard title="Total Policies" value={stats.totalPolicies.toString()} icon="ri-file-list-3-line" color="blue" />
          <StatsCard title="Total Broker Fee" value={`$${stats.totalBrokerFee.toLocaleString()}`} icon="ri-money-dollar-circle-line" color="green" />
          <StatsCard title="BF Average" value={`$${stats.bfAverage.toFixed(2)}`} icon="ri-bar-chart-line" color="purple" />
          <StatsCard title="Total Roadsides" value={stats.totalRoadsides.toString()} icon="ri-roadster-line" color="indigo" />
        </div>

        {/* Top Performers Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <i className="ri-trophy-line text-yellow-500 mr-3"></i>
            Top Performers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Most Policies */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Most Policies</h3>
                <i className="ri-trophy-fill text-3xl text-yellow-300"></i>
              </div>
              {topPerformers.mostPolicies ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                      {topPerformers.mostPolicies.photo ? (
                        <img
                          src={topPerformers.mostPolicies.photo}
                          alt={topPerformers.mostPolicies.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <i className="ri-user-line text-white text-xl"></i>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-lg truncate">{topPerformers.mostPolicies.name}</p>
                    </div>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded-lg p-3">
                    <p className="text-3xl font-black">{topPerformers.mostPolicies.policies.toLocaleString()}</p>
                    <p className="text-sm text-blue-100">Policies Sold</p>
                  </div>
                </div>
              ) : (
                <p className="text-blue-100">No data available</p>
              )}
            </div>

            {/* Most Roadsides */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Most Roadsides</h3>
                <i className="ri-car-line text-3xl text-green-200"></i>
              </div>
              {topPerformers.mostRoadsides ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                      {topPerformers.mostRoadsides.photo ? (
                        <img
                          src={topPerformers.mostRoadsides.photo}
                          alt={topPerformers.mostRoadsides.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <i className="ri-user-line text-white text-xl"></i>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-lg truncate">{topPerformers.mostRoadsides.name}</p>
                    </div>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded-lg p-3">
                    <p className="text-3xl font-black">{topPerformers.mostRoadsides.roadsides.toLocaleString()}</p>
                    <p className="text-sm text-green-100">Roadsides Sold</p>
                  </div>
                </div>
              ) : (
                <p className="text-green-100">No data available</p>
              )}
            </div>

            {/* Highest BF Total */}
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Highest BF Total</h3>
                <i className="ri-money-dollar-circle-fill text-3xl text-purple-200"></i>
              </div>
              {topPerformers.highestBFTotal ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                      {topPerformers.highestBFTotal.photo ? (
                        <img
                          src={topPerformers.highestBFTotal.photo}
                          alt={topPerformers.highestBFTotal.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <i className="ri-user-line text-white text-xl"></i>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-lg truncate">{topPerformers.highestBFTotal.name}</p>
                    </div>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded-lg p-3">
                    <p className="text-3xl font-black">
                      ${topPerformers.highestBFTotal.brokerFeeTotal.toLocaleString()}
                    </p>
                    <p className="text-sm text-purple-100">Broker Fee Total</p>
                  </div>
                </div>
              ) : (
                <p className="text-purple-100">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Individual Underwriting Assistant Performance */}
        <div className="mb-8">
          <IndividualPerformance data={data} />
        </div>

        {/* Individual Underwriting Assistant Performance (Cards) */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Individual Underwriting Assistant Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {employeeCardsData.map((employee) => (
              <div
                key={employee.id}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden bg-blue-100">
                    {employee.photo ? (
                      <img
                        src={employee.photo}
                        alt={employee.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = `<i class="ri-user-line text-blue-600 text-xl"></i>`;
                          }
                        }}
                      />
                    ) : (
                      <i className="ri-user-line text-blue-600 text-xl"></i>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold truncate text-gray-900">{employee.name}</h3>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600 flex items-center">📋 Policies:</span>
                    <span className="text-lg font-bold text-gray-900">{employee.policies}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600 flex items-center">📈 BF Average:</span>
                    <span className="text-lg font-bold text-gray-900 flex items-center">
                      {employee.brokerFeeAverage < 130 && <span className="mr-1">💩</span>}
                      ${employee.brokerFeeAverage}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600 flex items-center">💰 BF Total:</span>
                    <span className="text-lg font-bold text-gray-900">${employee.brokerFeeTotal.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600 flex items-center">🚗 Roadsides:</span>
                    <span className="text-lg font-bold text-gray-900">{employee.roadsides}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Rankings */}
        <div className="mb-8">
          <RankingTable employees={data} />
        </div>
      </div>
    </div>
  );
}
